import { Fragment, HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { CameraIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowModel, SortingState, useReactTable } from "@tanstack/react-table";

import { IPart, PartPosition, PartSide } from "../../models/Part";
import classNames from "../../utils/classNames";
import Pagination from "../shared/Tables/Pagination";
import MovePartsModal from "./MovePartsModal";
import PagedTable from "../shared/Tables/PagedTable";
import { PageMode } from "../../models/PagedTable";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useCategories from "../../data/useCategories";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import openInNewTab from "../../utils/openInNewTab";
import PartNotesModal from "./PartNotesModal";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IPart>();




export default function PartsEditorTable({ parts, setPart, additionalBody }: { parts: IPart[], setPart: any, additionalBody: Record<string, any> }) {
  const { bulkAcceptPartCategories } = useCategories();
  const [movePartsOpen, setMovePartsOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);

  const { addNotification } = useNotification();

  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);  
  const [notePart, setNotePart] = useState<IPart | undefined>(undefined);

  const cols = useMemo(
    () => [
    {
      id: 'select',
      header: ({ table }: any) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: any) => (
        <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        </div>
      ),
    },
    colHelper.display({
      id: "image",
      cell: (info) => (
        <span className="flex justify-center relative" >
          {info.row.original.cdnThumbnail ? (
            <>
              <img
                className="w-32 h-20 object-contain"
                src={`${info.row.original.cdnThumbnail != null ? info.row.original.cdnThumbnail : null}`}
              />
            </>
          ) : info.row.original.images.length > 0 && info.row.original.images[0].url != null ? (
            <img
              className="h-20 object-contain bg-white p-1 shadow-md hover:scale-[200%] hover:z-10 transition-transform duration-200 rounded-md"
              src={`${info.row.original.images[0].url != null ? info.row.original.images[0].url : null}`}
            />
          ) : (
            <CameraIcon className="w-14 text-gray-600" />
          )}
        </span>
      ),
    }),
    colHelper.accessor("titleSanitised", {
      header: "Title",
      sortingFn: "auto",
    }),
    colHelper.accessor("category.name", {
      header: "Category",
      sortingFn: "auto",
    }),
    colHelper.accessor("warehouseLocation.displayName", {
      header: "Location",
      sortingFn: "auto",
    }),
    colHelper.accessor("side", {
      header: "Side",
      cell: (info) => <span>{PartSide[info.getValue()]}</span>,
    }),
    colHelper.accessor("position", {
      header: "Position",
      cell: (info) => <span>{PartPosition[info.getValue()]}</span>,
    }),
    colHelper.accessor("price", {
      header: "Price",
      cell: (info) => <span>£{info.getValue()}</span>,
    }),
    colHelper.accessor("margin", {
      header: "Margin",
      cell: (info) => <span>£{info.getValue()}</span>,
    }),
    colHelper.accessor("partNumber", {
      header: "Part Number",
    }),
    colHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <span className="flex flex-col justify-center relative gap-2" >
          <ButtonInput 
            classes={""}
            onClick={() => {
              openInNewTab(`/stock/${info.row.original.id}`);
            }}
            label="Stock Page"
          />
          <ButtonInput 
            classes={"bg-slate-700 text-white hover:bg-slate-800"}
            onClick={() => {setNotePart(info.row.original)}}
            label="View Notes"
          />
        </span>
      ),
    }),
  ],
    []
  );

  const acceptCategories = () => {
    if (!toUpdate) return;

    const partIds = toUpdate.flatRows.map((row: any) => row.original.id);
    const body = {
      parts: partIds,
    };
    bulkAcceptPartCategories.mutate(body, {
      onSuccess: (success) => {
        addNotification({
          primaryText: success.message,
          variant: "success",
        });
        setToUpdate(undefined);
        setAcceptOpen(false);
      },
      onError: (error) => {
        console.error("Error accepting categories", error);
      }
    });

  }

  return (
    <>
      <MovePartsModal open={movePartsOpen} setOpen={setMovePartsOpen} parts={toUpdate?.flatRows.map((row: any) => row.original) ?? []} />
      <PartNotesModal open={notePart !== undefined} setOpen={() => setNotePart(undefined)} part={notePart} />
      <ConfirmModal 
        open={acceptOpen} 
        setOpen={setAcceptOpen} 
        title={"Accept part categories"}
        message={"This will accept the categories applied to the selected parts. Categories can be changed later."}
        confirmButtonText={"Accept"} 
        onConfirm={acceptCategories}
        isLoading={false}
      />
      <PagedTable
        key="parts-editor"
        url="/part/paginated"
        columns={cols}
        pageMode={PageMode.Stock}
        actionButtons={[
          { label: "Change category", onClick: (rows: RowModel<IPart>) => {setToUpdate(rows); setMovePartsOpen(true)}, disabledOnNoRows: true },
          { label: "Accept categories", onClick: (rows: RowModel<IPart>) => {setToUpdate(rows); setAcceptOpen(true)}, disabledOnNoRows: true },
        ]}
        showTotal={true}
        additionalBody={additionalBody}
      />
    </>
  );
}
